import {createFeatureSelector, createSelector} from '@ngrx/store';

import * as ApplicationSettingsActions from './application-settings.actions';
import {DataSourceConfig, DefaultSchemaName} from '../models/data-source-config.model';
import {
    Integration,
    INTEGRATION_TASKS_ORDER,
    PAGES_WHERE_INTEGRATION_TASK_ARE_SHOWN
} from '../models/integration.model';
import {Application} from '../models/application.model';
import {IntegrationTask} from '../models/integration-task.model';
import {IntegrationState} from '../models/integration-state.model';
import {IntegrationMode} from '../models/integration-mode.model';
import * as fromApp from '../../../../app.reducers';
import {FTPConfig} from '../models/ftp-config';
import {GSUITE_ROOT_IDENTIFIER, OrgItem} from '../models/orgItem.model';
import {BLACKBOARD_ROOT, OrgMapping, OrgMappingDuplicateSchool} from '../models/orgMapping.model';
import {DATA_SOURCE_TYPE, DataSourceType, SupportMappingRulesDataSourceTypes} from '../models/data-source-type.model';
import {ProcessConfig} from '../models/process-config.model';
import {
    ApplicationSettingsService,
    ONE_ROSTER_SOURCED_ID_DELIMITER,
    REQUEST_LOGIN_CREDENTIALS_ATTRIBUTES_MAP
} from '../application-settings.service';
import {DomainOfUniqueness} from '../models/domain-of-uniqueness.model';
import {copyData, RequestedFilterViewModel} from '@app/core/model/integration-models';
import {SchemaType} from '../models/schema-type.model';
import {MappingRule} from '@app/views/admin/application-settings/models/mapping-rules.model';
import {MetadataModel} from '@app/core/model/filtering/metadata-model';
import {EntityFilterModel} from '@app/core/model/filtering/entity-filter-model';
import {ENABLED_CTRL} from '@app/views/admin/application-settings/models/integration-task-map.model';
import {DictionaryModel} from '@app/views/admin/application-settings/models/dictionary.model';
import {RosterAccessProgress} from '@app/views/admin/application-settings/models/roster-access-progress.model';
import {QwiklabsCourse} from '@app/views/admin/application-settings/models/qwiklabs-course.model';
import {GSUITE_OU_ROOT_FRIENDLY_NAME} from '@app/views/admin/application-settings/models/gsuite-config.model';
import {Claim} from '@app/views/common/dialog-announcement/announcement.service';
import {
    EntityMasterFieldsModel,
    MasterFieldsMergingMetadataModel,
    MasterFieldsMergingRule,
    MasterFieldsModel,
    MergeMode
} from '@app/views/admin/application-settings/models/master-fields.model';
import {UserPermissionModel} from '@app/views/admin/application-settings/models/user-permission.model';
import {CustomerService} from '@app/core/service/customer.service';
import {GoogleClassroomDataSourceOptions} from '@app/views/admin/application-settings/models/google-clasroom-ds-options.model';
import {ENABLE_SUPPORT_INCREMENTAL_SYNC, FeatureToggleModel} from '@app/views/common/feature-toggles/featue-toggle.model';
import {FEATURE_TYPES} from '@app/views/admin/application-settings/models/application-properties.model';
import {SetProviderAllowedApplications, SetVendorAllowedApplications} from './application-settings.actions';
import {Webhook} from '@app/views/admin/application-settings/models/provisioning.model';
import {
    PIIShieldEntityAttributes
} from '@app/views/admin/application-settings/components/basic/pii-shield-level-info/entity-attributes/pii-shield-entities-attributes.component';

export interface ApplicationSettingsState extends fromApp.State {
    applicationSettings: State;
}

//
// export const ApplicationSettingsReducer: ActionReducerMap<ApplicationSettingsState> = {
//     applicationSettings: applicationSettingsReducer,
//     auth: auth.authReducer,
//     search: search.searchReducer
// };

export const getApplicationSettings = createFeatureSelector<State>('applicationSettings');

export const getUserPermissions = createSelector(getApplicationSettings, (state: State) => state.userPermission);
export const getDefaultApplications = createSelector(getApplicationSettings, (state: State) => state.defaultApplications);
export const getAllowedApplications = createSelector(getApplicationSettings, (state: State) => state.allowedApplications);
export const getFTPConfig = createSelector(getApplicationSettings, (state: State) => state.ftpConfig);
export const getDataSourceConfig = createSelector(getApplicationSettings, (state: State) => state.dataSourceConfig);
export const getMappingRulesArchiveOU = createSelector(getApplicationSettings, (state: State) => state.mappingRulesArchiveOU);
export const getApplication = createSelector(getApplicationSettings, (state: State) => state.application);
export const getActualPrivacyPolicyDocument = createSelector(getApplicationSettings, (state: State) => state.actualPrivacyPolicyDocument);
export const getIsDeletingApplication = createSelector(getApplicationSettings, (state: State) => state.isDeletingApplication);
export const getIntegration = createSelector(getApplicationSettings, (state: State) => state.integration);
export const getOrgsMapping = createSelector(getApplicationSettings, (state: State) => state.orgsMapping);
export const getPredicatesMetadata = createSelector(getApplicationSettings, (state: State) => state.predicatesMetadata);
export const getFiltrations = createSelector(getApplicationSettings, (state: State) => state.filtrations);
export const getSsoFiltrations = createSelector(getApplicationSettings, (state: State) => state.ssoFiltrations);
export const getMappingRules = createSelector(getApplicationSettings, (state: State) => state.mappingRules);
export const getMasterFields = createSelector(getApplicationSettings, (state: State) => state.masterFields);
export const getIntegrationMode = createSelector(getApplicationSettings, (state: State) => state.integrationMode);
export const getIntegrationState = createSelector(getApplicationSettings, (state: State) => state.integrationState);
export const getIsLoading = createSelector(getApplicationSettings, (state: State) => state.isLoading);
export const getIsSavingFTPUser = createSelector(getApplicationSettings, (state: State) => state.isSavingFTPUser);
export const getIsOriginalIntegration = createSelector(getApplicationSettings, (state: State) => state.isOriginalIntegration);
export const getRosterAccessProgress = createSelector(getApplicationSettings, (state: State) => state.rosterAccessProgress);
export const getQwiklabsCourses = createSelector(getApplicationSettings, (state: State) => state.qwiklabsCourses);
export const getGoogleClassroomDSOptions = createSelector(getApplicationSettings, (state: State) =>
    state.googleClassroomDSOptions);
export const getWebhook = createSelector(getApplicationSettings, (state: State) =>
    state.webhook);
export const getPIIShieldConfigEntitiesAttributes = createSelector(getApplicationSettings, (state: State) =>
    state.piiShieldConfigEntitiesAttributes);
export const getApplicationSettingsFeatureToggles = createSelector(getApplicationSettings, (state: State) => state.applicationSettingsFeatureToggles);
export const getError = createSelector(getApplicationSettings, (state: State) => state.error);
export const getActivatedMergingRules = createSelector(getApplicationSettings,
    (state: State) => state.integration.tasks.reduce(((accumulator, currentValue) => {
        if (currentValue.domainsOfUniqueness) {
            const activeDomainsOfUniqueness = currentValue.domainsOfUniqueness
                .filter((domainOfUniqueness: DomainOfUniqueness) => domainOfUniqueness.activated);
            return [...accumulator, ...activeDomainsOfUniqueness];
        } else {
            return accumulator;
        }
    }), [])
);

export interface State {
    defaultApplications: Application[];
    allowedApplications: Application[];
    ftpConfig: FTPConfig;
    dataSourceConfig: DataSourceConfig;
    mappingRulesArchiveOU: string;
    application: Application;
    actualPrivacyPolicyDocument: Claim;
    isDeletingApplication: boolean;
    integration: Integration;
    orgsMapping: OrgMapping[];
    predicatesMetadata: MetadataModel[];
    filtrations: RequestedFilterViewModel[];
    ssoFiltrations: RequestedFilterViewModel[];
    mappingRules: MappingRule[];
    masterFields: MasterFieldsModel;
    integrationMode: IntegrationMode;
    integrationState: IntegrationState;
    isLoading: boolean;
    isSavingFTPUser: boolean;
    isOriginalIntegration: boolean;
    rosterAccessProgress: RosterAccessProgress;
    qwiklabsCourses: QwiklabsCourse[];
    googleClassroomDSOptions: GoogleClassroomDataSourceOptions;
    webhook: Webhook;
    piiShieldConfigEntitiesAttributes: PIIShieldEntityAttributes[];
    applicationSettingsFeatureToggles: { application?: FeatureToggleModel[], integration?: FeatureToggleModel[] };
    userPermission: UserPermissionModel;
    error: any;
}

export const initialState: State = {
    defaultApplications: <Application[]>[],
    allowedApplications: <Application[]>[],
    ftpConfig: new FTPConfig(),
    dataSourceConfig: new DataSourceConfig(),
    mappingRulesArchiveOU: null,
    application: new Application(),
    actualPrivacyPolicyDocument: null,
    isDeletingApplication: false,
    integration: new Integration(),
    orgsMapping: null,
    predicatesMetadata: <MetadataModel[]>[],
    filtrations: null,
    ssoFiltrations: null,
    mappingRules: null,
    masterFields: null,
    integrationMode: null,
    integrationState: null,
    isLoading: false,
    isSavingFTPUser: false,
    isOriginalIntegration: false,
    rosterAccessProgress: new RosterAccessProgress(100),
    qwiklabsCourses: <QwiklabsCourse[]>[],
    googleClassroomDSOptions: new GoogleClassroomDataSourceOptions(),
    webhook: null,
    piiShieldConfigEntitiesAttributes: [],
    applicationSettingsFeatureToggles: {application: <FeatureToggleModel[]>[], integration: <FeatureToggleModel[]>[]},
    userPermission: new UserPermissionModel(),
    error: null
};

export function applicationSettingsReducer(state = initialState, action: ApplicationSettingsActions.ApplicationSettingsActions): State {
    switch (action.type) {
        case (ApplicationSettingsActions.ASSIGN_APPLICATION):
            const application = Object.assign({}, state.application, action.payload);
            return <State>{
                ...state,
                application: application
            };
        case (ApplicationSettingsActions.SET_USER_PERMISSIONS):
            return <State>{
                ...state,
                userPermission: action.payload
            };
        case (ApplicationSettingsActions.SET_FTP_CONFIG):
            return <State>{
                ...state,
                ftpConfig: action.payload
            };
        case (ApplicationSettingsActions.ASSIGN_FTP_CONFIG): {
            const userPermissions = [...state.ftpConfig.userPermissions];
            if (action.payload.oldUsername) {
                const index = userPermissions.findIndex(user => user.username === action.payload.oldUsername);
                if (action.payload.ftpUser) {
                    // edit FTP user
                    userPermissions[index] = action.payload.ftpUser;
                } else {
                    // delete FTP user
                    if (index > -1) {
                        userPermissions.splice(index, 1);
                    }
                }
            } else {
                // create FTP user
                userPermissions.push(
                    action.payload.ftpUser
                );
            }
            const ftpConfig = Object.assign({}, state.ftpConfig, {userPermissions: userPermissions});
            return <State>{
                ...state,
                ftpConfig: ftpConfig
            };
        }
        case (ApplicationSettingsActions.SET_IS_DELETING_APPLICATION):
            return <State>{
                ...state,
                isDeletingApplication: action.payload
            };
        case (ApplicationSettingsActions.SET_DEFAULT_APPLICATIONS):
            return <State>{
                ...state,
                defaultApplications: action.payload
            };
        case (ApplicationSettingsActions.SET_PROVIDER_ALLOWED_APPLICATIONS):
            const defaultApplications: Application[] = (<SetProviderAllowedApplications>action).payload;

            const importIntegrationMode = IntegrationMode.IMPORT;
            const allowedProviderDataSources: DataSourceType[] = [
                DataSourceType.OneRoster,
                DataSourceType.PowerSchool,
                DataSourceType.SFTP,
                DataSourceType.Skyward,
                DataSourceType.CsvUpload,
                DataSourceType.Inow,
                DataSourceType.Illuminates,
                DataSourceType.Coursera,
                DataSourceType.LTI,
                DataSourceType.Gg4lPassport,
                DataSourceType.Veracross,
                DataSourceType.LDAP,
                DataSourceType.Qwiklabs,
                DataSourceType.GoogleClassroom,
                DataSourceType.Aeries,
                DataSourceType.GSuite,
                DataSourceType.OpenApply,
                DataSourceType.ManageBac,
                DataSourceType.Blackboard,
            ];

            let allowedProviderApplications = defaultApplications.filter((app: Application) => {
                if ((app.modes && (app.modes.indexOf(<string>importIntegrationMode) > -1))
                    || (<DataSourceType>app.dataSourceType === DataSourceType.LTI)
                    || (<DataSourceType>app.dataSourceType === DataSourceType.GoogleClassroom)
                ) {
                    return allowedProviderDataSources.indexOf(<DataSourceType>app.dataSourceType) >= 0;
                } else {
                    return false;
                }
            });

            allowedProviderApplications = allowedProviderApplications.map((app: Application) => {
                // ToDo: delete this when LDAP Export & Sync will be allowed
                if (<DataSourceType>app.dataSourceType === DataSourceType.LDAP) {
                    app.modes = app.modes.filter((mode: string) =>
                        <IntegrationMode>mode === IntegrationMode.IMPORT
                    );
                }
                return app;
            });

            allowedProviderApplications.sort((ds1: Application, ds2: Application) =>
                ds1.name.toLowerCase().localeCompare(ds2.name.toLowerCase())
            );

            return <State>{
                ...state,
                allowedApplications: allowedProviderApplications
            };
        case (ApplicationSettingsActions.SET_VENDOR_ALLOWED_APPLICATIONS):
            const defaultVendorApplications: Application[] = (<SetVendorAllowedApplications>action).payload;

            const exportIntegrationMode = IntegrationMode.EXPORT;
            const allowedVendorDataSources: DataSourceType[] = [
                DataSourceType.DataAPI,
                DataSourceType.SFTP
                // DataSourceType.EdFi
            ];

            let allowedVendorApplications = defaultVendorApplications.filter((app: Application) => {
                if (app.modes && (app.modes.indexOf(<string>exportIntegrationMode) > -1)) {
                    return allowedVendorDataSources.indexOf(<DataSourceType>app.dataSourceType) >= 0;
                } else {
                    return false;
                }
            }).filter((app: Application) =>
                CustomerService.checkIsSupportSsoAppFeature()
                || <DataSourceType>app.dataSourceType !== DataSourceType.DataAPI
                || app.featureType !== <string>FEATURE_TYPES.SSO
            );

            allowedVendorApplications = allowedVendorApplications.map((app: Application) => {
                app.modes = [exportIntegrationMode];
                return app;
            });

            allowedVendorApplications.sort((ds1: Application, ds2: Application) =>
                ds1.name.toLowerCase().localeCompare(ds2.name.toLowerCase())
            );

            return <State>{
                ...state,
                allowedApplications: allowedVendorApplications
            };
        case (ApplicationSettingsActions.ASSIGN_DATA_SOURCE_CONFIG): {
            const dataSourceConfig = Object.assign({}, state.dataSourceConfig, action.payload);
            return <State>{
                ...state,
                dataSourceConfig: dataSourceConfig,
                mappingRulesArchiveOU: dataSourceConfig['archiveOu']
            };
        }
        case (ApplicationSettingsActions.ASSIGN_MAPPING_RULES_ARCHIVE_OU): {
            return <State>{
                ...state,
                mappingRulesArchiveOU: action.payload
            };
        }
        case (ApplicationSettingsActions.SET_INTEGRATION): {
            // if ((<Integration>action.payload).target.dataSourceType === DataSourceType.SFTP) {
            //     // Temporary fix - waiting fix on backend
            //     action.payload.hasMultipleOrgs = false;
            // }
            // Extend task mapping model by full attribute model
            const integration = <Integration>action.payload;

            if (integration.integrationId) {
                const edutoneDS: string = (integration.source.dataSourceType === DataSourceType.EduTone) ? 'source' : 'target';
                const otherDS = (integration.source.dataSourceType === DataSourceType.EduTone) ? 'target' : 'source';

                integration.tasks = integration.tasks ? integration.tasks : <IntegrationTask[]>[];
                if (integration.tasks) {
                    integration.tasks = integration.tasks.map((task: IntegrationTask) =>
                        ApplicationSettingsService.getExtendedIntegrationTask(task, integration));
                }

                integration.tasks.sort((task1: IntegrationTask, task2: IntegrationTask) =>
                    (INTEGRATION_TASKS_ORDER[task1[edutoneDS + 'Type']] || 100)
                    - (INTEGRATION_TASKS_ORDER[task2[edutoneDS + 'Type']] || 100));

                const applicationFeatureToggles = state?.applicationSettingsFeatureToggles?.application;
                const isSupportIncrementalSyncFeatureToggleIsActive = applicationFeatureToggles?.some((featureToggle: FeatureToggleModel) =>
                    (featureToggle.keyName === ENABLE_SUPPORT_INCREMENTAL_SYNC)
                    && featureToggle.enabled
                );

                integration.processConfigModel = ApplicationSettingsService.getExtendedProcessConfig(
                    integration.processConfigModel,
                    integration[otherDS].dataSourceType,
                    integration.mode,
                    integration[otherDS].defaultSchemaName,
                    isSupportIncrementalSyncFeatureToggleIsActive
                );

                if (integration.target.schemaTypeModelList && integration.target.schemaTypeModelList.length > 1) {
                    integration.target.schemaTypeModelList.sort((schemaA: SchemaType, schemaB: SchemaType) =>
                        ApplicationSettingsService.getEntityName(schemaA.denom).toLowerCase()
                            .localeCompare(ApplicationSettingsService.getEntityName(schemaB.denom).toLowerCase())
                    );
                }
            }

            return <State>{
                ...state,
                integration: integration
            };
        }
        case (ApplicationSettingsActions.ASSIGN_INTEGRATION_TASK): {
            const integration: Integration = <Integration>{...state.integration};
            const otherDS = (integration.source.dataSourceType === DataSourceType.EduTone) ? 'target' : 'source';

            if (integration.tasks) {
                integration.tasks = integration.tasks.map((task: IntegrationTask) =>
                    (task.taskId === action.payload.taskId)
                        ? ApplicationSettingsService.getExtendedIntegrationTask(Object.assign({}, task, action.payload), integration)
                        : task
                );
            }

            return <State>{
                ...state,
                integration: integration
            };
        }
        case (ApplicationSettingsActions.INTEGRATION_TASKS_DELETED): {
            const integration: Integration = <Integration>{...state.integration};
            const deletedIntegrationTasks = action.payload;

            if (integration.tasks) {
                integration.tasks = integration.tasks.filter((task: IntegrationTask) =>
                    deletedIntegrationTasks.every((deletedTask: IntegrationTask) => task.taskId !== deletedTask.taskId)
                );
            }

            return <State>{
                ...state,
                integration: integration
            };
        }
        case (ApplicationSettingsActions.ASSIGN_INTEGRATION_TASKS): {
            const integration: Integration = <Integration>{...state.integration};
            const otherDS = (integration.source.dataSourceType === DataSourceType.EduTone) ? 'target' : 'source';

            integration.tasks = ApplicationSettingsService.assignIntegrationTasks(integration, action.payload, state.application.guid);

            return <State>{
                ...state,
                integration: integration
            };
        }
        case (ApplicationSettingsActions.SET_INTEGRATION_RULES): {
            const integration: Integration = <Integration>{...state.integration};
            let filtrations: RequestedFilterViewModel[] = [];
            let ssoFiltrations: RequestedFilterViewModel[] = [];
            let mappingRules: MappingRule[] = state.mappingRules ? <MappingRule[]>[...state.mappingRules] : [];

            if (integration.integrationId && integration.source && integration.source.dataSourceType) {
                const edutoneDS: string = integration.integrationId && (integration.source.dataSourceType === DataSourceType.EduTone)
                    ? 'source' : 'target';
                const otherDS = (integration.source.dataSourceType === DataSourceType.EduTone) ? 'target' : 'source';
                const integrationDataSourceType = (<DataSourceConfig>integration[otherDS]).dataSourceType;
                // const transformations: EntityFilterModel[] = [];
                const isSupportMappingRules = integration[otherDS] && SupportMappingRulesDataSourceTypes.includes(integrationDataSourceType);
                let newMappingRules: MappingRule[] = [];

                if (integration.tasks) {
                    integration.tasks = integration.tasks.map((task: IntegrationTask) => {
                        for (const taskId in action.payload) {
                            if (taskId === task.taskId) {
                                if (action.payload[taskId]?.transformations) {
                                    action.payload[taskId].transformations.forEach((transformRule: EntityFilterModel) => {
                                        if (transformRule.customValues) {
                                            // const customValuesMap: Map<string, Argument> = new Map();
                                            for (const key of Object.keys(transformRule.customValues)) {
                                                for (const [oldValue, newValue] of REQUEST_LOGIN_CREDENTIALS_ATTRIBUTES_MAP) {
                                                    transformRule.customValues[key].name =
                                                        transformRule.customValues[key].name.split(oldValue).join(newValue);
                                                    transformRule.customValues[key].id =
                                                        transformRule.customValues[key].id.split(oldValue).join(newValue);
                                                }
                                                // customValuesMap.set(key, transformRule.customValues[key]);
                                            }
                                            // transformRule.customValues = customValuesMap;
                                        }
                                    });
                                }

                                task = Object.assign(
                                    task,
                                    {
                                        validations: action.payload[taskId].validations ? action.payload[taskId].validations : task.validations,
                                        transformations: action.payload[taskId].transformations ? action.payload[taskId].transformations : task.transformations
                                    }
                                );

                                filtrations = RequestedFilterViewModel.processFiltration(
                                    filtrations, action.payload[taskId].filtrations, task, edutoneDS);
                                ssoFiltrations = RequestedFilterViewModel.processFiltration(
                                    ssoFiltrations, action.payload[taskId].ssoFiltrations, task, edutoneDS);
                                // transformations.push(action.payload[taskId].transformations);

                                if (isSupportMappingRules
                                    && action.payload[taskId].streamTransformations
                                    && action.payload[taskId].streamTransformations.length > 0) {
                                    newMappingRules = MappingRule.processMappingRule(newMappingRules,
                                        action.payload[taskId].streamTransformations, task, integrationDataSourceType, edutoneDS);
                                }
                            }
                        }
                        return task;
                    });
                }

                mappingRules = newMappingRules.length === 0 ? mappingRules : newMappingRules;

                if (filtrations.length > 0) {
                    filtrations = filtrations.map((filter: RequestedFilterViewModel) => {
                        if (filter) {
                            if (!filter.linkedEntityTypes) {
                                filter.linkedEntityTypes = <string[]>[];
                            }

                            if (filter.linkedEntityTypes && filter.linkedEntityTypes.length > 1) {
                                filter.linkedEntityTypes.sort((linkedEntityA: string, linkedEntityB: string) =>
                                    (INTEGRATION_TASKS_ORDER[linkedEntityA] || 100) - (INTEGRATION_TASKS_ORDER[linkedEntityB] || 100)
                                );
                            }
                        }

                        return <RequestedFilterViewModel>filter;
                    });

                    RequestedFilterViewModel.sortFiltrations(filtrations);

                    filtrations = filtrations.map((filter: RequestedFilterViewModel, filterIndex: number) => {
                        filter.filterIndex = filterIndex;
                        return filter;
                    });
                } else {
                    filtrations = state.filtrations ? state.filtrations : [];
                }

                if (ssoFiltrations.length > 0) {
                    RequestedFilterViewModel.sortFiltrations(ssoFiltrations);

                    ssoFiltrations = ssoFiltrations.map((filter: RequestedFilterViewModel, filterIndex: number) => {
                        filter.filterIndex = filterIndex;
                        return filter;
                    });
                } else {
                    ssoFiltrations = state.ssoFiltrations ? state.ssoFiltrations : [];
                }
            }

            return <State>{
                ...state,
                integration: integration,
                filtrations: filtrations,
                ssoFiltrations: ssoFiltrations,
                mappingRules: mappingRules
            };
        }
        case (ApplicationSettingsActions.ASSIGN_INTEGRATION): {
            return <State>{
                ...state,
                integration: action.payload
            };
        }
        case (ApplicationSettingsActions.SET_INTEGRATION_ORG_MAPPING): {
            if (!state.integration.integrationId) {
                return state;
            }
            let orgsMapping: OrgMapping[] = <OrgMapping[]>[];
            const newIntegration: Integration = <Integration>{...state.integration};
            newIntegration.organizationsMappingModel = action.payload?.organizationsMapping;

            const otherDS: string = (state.integration.source.dataSourceType === DataSourceType.EduTone) ? 'target' : 'source';
            let mainOrgsPrefix: string = (state.integration.source.dataSourceType === DataSourceType.EduTone)
                ? (state.integration.target.dataSourceType === DataSourceType.SFTP ? 'edutone' : 'target')
                : newIntegration.source.dataSourceType === DataSourceType.Qwiklabs ? 'edutone' : 'source';
            let isMappingToRoot: boolean;

            if (newIntegration.source.dataSourceType === DataSourceType.Blackboard) {
                // filter edutone schools in sourceOrganizations
                const edutoneOrganizationsObj = {};
                newIntegration.organizationsMappingModel.edutoneOrganizations.forEach((org: OrgItem) =>
                    edutoneOrganizationsObj[org.identifier] = org
                );
                newIntegration.organizationsMappingModel.sourceOrganizations =
                    newIntegration.organizationsMappingModel.sourceOrganizations.filter((org: OrgItem) =>
                        !edutoneOrganizationsObj[org.identifier]
                    );

                if (action.payload?.blackboardViewOptionControl) {
                    isMappingToRoot = action.payload?.blackboardViewOptionControl.value === BLACKBOARD_ROOT;
                } else {
                    isMappingToRoot = ApplicationSettingsService.getIsMappingToRootBlackboardSchoolsMapping(
                        newIntegration.organizationsMappingModel
                    );
                }
                if (isMappingToRoot) {
                    mainOrgsPrefix = 'edutone';

                    newIntegration.organizationsMappingModel.mappingList
                        .forEach((orgMapping: OrgMapping) => orgMapping.source = orgMapping.edutone);
                }
            }

            if (newIntegration.organizationsMappingModel
                && newIntegration.organizationsMappingModel.edutoneOrganizations
                && newIntegration.organizationsMappingModel.edutoneOrganizations.length > 1) {
                newIntegration.organizationsMappingModel.edutoneOrganizations.sort((a: OrgItem, b: OrgItem) =>
                    a.name.localeCompare(b.name, 'en', {sensitivity: 'base', numeric: true}));
            }

            if (newIntegration.organizationsMappingModel
                && newIntegration.organizationsMappingModel[otherDS + 'Organizations']
                && newIntegration.organizationsMappingModel[otherDS + 'Organizations'].length > 1
                && newIntegration[otherDS].dataSourceType === DataSourceType.GSuite) {
                const root = newIntegration.organizationsMappingModel[otherDS + 'Organizations']
                    .find((org: OrgItem) => org.identifier === GSUITE_ROOT_IDENTIFIER);
                if (root) {
                    root.name = GSUITE_OU_ROOT_FRIENDLY_NAME;
                }
            }

            if (newIntegration.organizationsMappingModel
                && newIntegration.organizationsMappingModel.mappingList) {
                newIntegration.organizationsMappingModel.mappingList
                    .forEach((orgMapping: OrgMapping) => orgMapping.enabled = orgMapping.existing = true);

                if (newIntegration.organizationsMappingModel[mainOrgsPrefix + 'Organizations']
                    && newIntegration.organizationsMappingModel[mainOrgsPrefix + 'Organizations'].length > 0) {

                    const IS_AMPLIFY_CUSTOMER = CustomerService.checkIsAmplifyCustomer();
                    // check is organization duplicated (for OneRoster data sources)
                    const sourcedIdbj = {}; // { sourcedId: source.name[] }
                    const oneRosterIdentifierObj = {}; // { OneRoster Identifier: source.name[] }
                    const isOneRosterSchema = state.dataSourceConfig.dataSourceType === DATA_SOURCE_TYPE.SFTP
                        && ((state.integration[otherDS].defaultSchemaName === DefaultSchemaName.ONE_ROSTER_10)
                            || (state.integration[otherDS].defaultSchemaName === DefaultSchemaName.ONE_ROSTER_11)
                            || (state.integration[otherDS].defaultSchemaName === DefaultSchemaName.ONE_ROSTER_12));

                    if (IS_AMPLIFY_CUSTOMER && isOneRosterSchema) {
                        newIntegration.organizationsMappingModel[mainOrgsPrefix + 'Organizations'] =
                            newIntegration.organizationsMappingModel[mainOrgsPrefix + 'Organizations']
                                .map((org: OrgItem) => {
                                    if (org.identifier.includes(ONE_ROSTER_SOURCED_ID_DELIMITER)) {
                                        const [sourcedId, oneRosterIdentifier] = org.identifier.split(ONE_ROSTER_SOURCED_ID_DELIMITER);

                                        if (sourcedIdbj[sourcedId]) {
                                            sourcedIdbj[sourcedId].push(org.name);
                                        } else {
                                            sourcedIdbj[sourcedId] = [org.name];
                                        }

                                        if (oneRosterIdentifierObj[oneRosterIdentifier]) {
                                            oneRosterIdentifierObj[oneRosterIdentifier].push(org.name);
                                        } else {
                                            oneRosterIdentifierObj[oneRosterIdentifier] = [org.name];
                                        }
                                    }
                                    return org;
                                });
                    }

                    orgsMapping = newIntegration.organizationsMappingModel[mainOrgsPrefix + 'Organizations']
                        .map((org: OrgItem, index: number) => {

                            // check is organization duplicated (for OneRoster data sources)
                            let duplicateSchool: OrgMappingDuplicateSchool;

                            if (org.identifier.includes(ONE_ROSTER_SOURCED_ID_DELIMITER)) {
                                const [sourcedId, oneRosterIdentifier] = org.identifier.split(ONE_ROSTER_SOURCED_ID_DELIMITER);
                                org.identifier = sourcedId;
                                org.oneRosterIdentifier = oneRosterIdentifier;

                                if (IS_AMPLIFY_CUSTOMER && isOneRosterSchema && sourcedIdbj[sourcedId] && oneRosterIdentifierObj[oneRosterIdentifier]) {
                                    const duplicateOrgNames = sourcedIdbj[sourcedId].concat(oneRosterIdentifierObj[oneRosterIdentifier]);

                                    if (((sourcedIdbj[sourcedId].length > 1)
                                            && (sourcedId !== null)
                                            && (sourcedId !== undefined)
                                            && (sourcedId !== ''))
                                        || ((oneRosterIdentifierObj[oneRosterIdentifier].length > 1)
                                            && (oneRosterIdentifier !== null)
                                            && (oneRosterIdentifier !== undefined)
                                            && (oneRosterIdentifier !== ''))) {

                                        duplicateOrgNames.sort((a: string, b: string) =>
                                            a.localeCompare(b, 'en', {sensitivity: 'base', numeric: true}));
                                        duplicateSchool = new OrgMappingDuplicateSchool(duplicateOrgNames[0], oneRosterIdentifier,
                                            sourcedIdbj[sourcedId].length > 1, oneRosterIdentifierObj[oneRosterIdentifier].length > 1);
                                    }
                                }
                            }

                            let orgMap: OrgMapping = newIntegration.organizationsMappingModel.mappingList
                                .find((orgMapping: OrgMapping) => orgMapping[mainOrgsPrefix]?.identifier === org.identifier);
                            if (!orgMap) {
                                orgMap = new OrgMapping(null, null, null, null, false);
                                orgMap[mainOrgsPrefix] = org;
                            }
                            orgMap.duplicateSchool = duplicateSchool;
                            return orgMap;
                        });

                } else {
                    if (state.integration.source.dataSourceType === DataSourceType.EduTone
                        && state.integration.target.dataSourceType === DataSourceType.SFTP) {
                        orgsMapping = newIntegration.organizationsMappingModel.edutoneOrganizations
                            .map((org: OrgItem) => {
                                const orgMap: OrgMapping = newIntegration.organizationsMappingModel.mappingList
                                    .find((orgMapping: OrgMapping) => orgMapping.edutone.identifier === org.identifier);
                                return orgMap ? orgMap : new OrgMapping(org, null, null, null, false);
                            });
                    }
                }

                if (newIntegration.organizationsMappingModel.mappingList.length > 0) {
                    newIntegration.organizationsMappingModel.mappingList.forEach((orgMapping: OrgMapping) => {
                        if (orgMapping.edutone) {

                            const orgMappingSaveKeyName = isMappingToRoot ? 'source' : mainOrgsPrefix;

                            const mappedSchoolIsMissingInOtherDSOrganizations = !orgsMapping.some((orgMap: OrgMapping) =>
                                orgMap[orgMappingSaveKeyName]
                                && (orgMap[orgMappingSaveKeyName]?.identifier === orgMapping[orgMappingSaveKeyName]?.identifier)
                            );
                            if (mappedSchoolIsMissingInOtherDSOrganizations) {
                                orgsMapping.push(orgMapping);
                            }

                            if (state.integration.source.dataSourceType === DataSourceType.Blackboard) {
                                // fix to show parentId in orgMapping.source org
                                const orgMappingSourceCopy = copyData(newIntegration.organizationsMappingModel.sourceOrganizations
                                    .find((org: OrgItem) => org?.identifier === orgMapping?.source?.identifier));
                                orgMapping.source = orgMappingSourceCopy ? orgMappingSourceCopy : orgMapping.source;
                            } else {

                                // not for DataSourceType.Blackboard
                                // in case there are multiple mappings for one source (orgMappingSaveKeyName) organization
                                const mappingListOfCurrentOrg = orgsMapping.filter((orgMap: OrgMapping) =>
                                    orgMap[orgMappingSaveKeyName]
                                    && (orgMap[orgMappingSaveKeyName]?.identifier === orgMapping[orgMappingSaveKeyName]?.identifier)
                                );
                                mappingListOfCurrentOrg.forEach((savedOrgMap: OrgMapping) => {
                                    if (savedOrgMap.edutone.identifier !== orgMapping.edutone.identifier) {
                                        orgsMapping.push(orgMapping);
                                    }
                                });
                            }
                        }
                    });
                }
            }

            if (orgsMapping.length > 1) {
                orgsMapping.sort((a: OrgMapping, b: OrgMapping) => {
                    if (a.duplicateSchool || b.duplicateSchool) {
                        if (a.duplicateSchool && b.duplicateSchool) {
                            return a.duplicateSchool.firstSourceSchoolName.localeCompare(
                                b.duplicateSchool.firstSourceSchoolName, 'en', {sensitivity: 'base', numeric: true});
                        } else {
                            return a.duplicateSchool ? -1 : 1;
                        }
                    }
                    if (!a[mainOrgsPrefix] || !a[mainOrgsPrefix].name) {
                        return -1;
                    }
                    if (!b[mainOrgsPrefix] || !b[mainOrgsPrefix].name) {
                        return 1;
                    }
                    return a[mainOrgsPrefix].name.localeCompare(b[mainOrgsPrefix].name, 'en', {sensitivity: 'base', numeric: true});
                });
            }

            if (orgsMapping.length > 0) {
                orgsMapping = orgsMapping
                    .map((orgMapping: OrgMapping, orgMappingIndex: number) => Object.assign({}, orgMapping, {orgMappingIndex: orgMappingIndex, isClicked: false}));
            }

            return <State>{
                ...state,
                integration: newIntegration,
                orgsMapping: orgsMapping
            };
        }
        case (ApplicationSettingsActions.INTEGRATION_ORG_MAPPING_DELETED): {
            const otherDS: string = (state.integration.source.dataSourceType === DataSourceType.EduTone) ? 'target' : 'source';
            const newIntegration: Integration = <Integration>{...state.integration};
            let orgsMapping: OrgMapping[] = <OrgMapping[]>[...action.payload.orgsMapping];
            const isBlackboardSchoolSelectorView = action.payload?.blackboardViewOptionControl?.value === BLACKBOARD_ROOT;
            const orgMappingSaveKeyName = isBlackboardSchoolSelectorView ? 'source' : 'edutone';

            if (action.payload) {

                const deletedOrgMapping = orgsMapping[(<OrgMapping>action.payload.orgMapping).orgMappingIndex];
                if (deletedOrgMapping) {
                    newIntegration.organizationsMappingModel.mappingList = newIntegration.organizationsMappingModel.mappingList
                        .filter((orgMapping: OrgMapping) =>
                            orgMapping[orgMappingSaveKeyName]
                            && (!deletedOrgMapping
                                || (deletedOrgMapping && !deletedOrgMapping[orgMappingSaveKeyName])
                                || (deletedOrgMapping
                                    && deletedOrgMapping[orgMappingSaveKeyName]
                                    && (orgMapping[orgMappingSaveKeyName]?.identifier !== deletedOrgMapping[orgMappingSaveKeyName]?.identifier)))
                        );

                    const deletedOrgKey =
                        (((state.integration.source.dataSourceType === DataSourceType.EduTone)
                                && (state.integration.target.dataSourceType === DataSourceType.SFTP))
                            || isBlackboardSchoolSelectorView)
                            ? 'edutone'
                            : otherDS;
                    const isDeletedOrganizationExisting = state.integration.organizationsMappingModel[deletedOrgKey + 'Organizations']
                        .find((org: OrgItem) => org.identifier === deletedOrgMapping[deletedOrgKey].identifier);
                    if (isDeletedOrganizationExisting) {

                        if ((state.integration.organizationsMappingModel[otherDS + 'Organizations'])
                            && (state.integration.organizationsMappingModel[otherDS + 'Organizations'].length > 0)) {
                            deletedOrgMapping[orgMappingSaveKeyName] = null;
                            deletedOrgMapping.existing = false;
                            deletedOrgMapping.customOrgName = null;
                        } else {
                            if ((state.integration.source.dataSourceType === DataSourceType.EduTone)
                                && (state.integration.target.dataSourceType === DataSourceType.SFTP)) {
                                deletedOrgMapping.existing = false;
                                deletedOrgMapping.enabled = false;
                            }
                        }
                    } else {

                        orgsMapping.splice(deletedOrgMapping.orgMappingIndex, 1);

                        if (orgsMapping.length > 0) {
                            orgsMapping = orgsMapping
                                .map((orgMapping: OrgMapping, orgMappingIndex: number) => Object.assign({}, orgMapping, {orgMappingIndex: orgMappingIndex, isClicked: false}));
                        }
                    }
                }
            }

            return <State>{
                ...state,
                orgsMapping: orgsMapping,
                integration: newIntegration
            };
        }
        case (ApplicationSettingsActions.ASSIGN_INTEGRATION_ORGS_SELECTOR): {
            const orgsMapping = Object.assign([], state.orgsMapping);
            orgsMapping
                .filter((orgMapping: OrgMapping) => !orgMapping.existing)
                .forEach((orgMapping) => {
                    orgMapping.enabled = action.payload.formControlValues[`${orgMapping.edutone.identifier}Ctrl`];
                });
            return <State>{
                ...state,
                orgsMapping: orgsMapping
            };
        }
        case (ApplicationSettingsActions.ASSIGN_INTEGRATION_ORGS_MAPPING): {
            return <State>{
                ...state,
                orgsMapping: action.payload
            };
        }
        case (ApplicationSettingsActions.ASSIGN_INTEGRATION_TASK_MAPPINGS): {
            const integration = Object.assign({}, state.integration);

            if (integration.tasks) {
                integration.tasks = integration.tasks
                    .map((task) => {
                        if (task.taskId === action.payload.taskId) {
                            task.mappings.forEach((mapping) => {
                                mapping.enabled = action.payload.formControlValues[`${mapping.mappingIndex}${ENABLED_CTRL}`];
                            });
                        }
                        return task;
                    });
            }

            return <State>{
                ...state,
                integration: integration
            };
        }
        case (ApplicationSettingsActions.ASSIGN_DICTIONARY_BY_NAME): {
            const integration = Object.assign({}, state.integration);
            if (!integration.dictionaries) {
                integration.dictionaries = [];
            }

            const dictionaryIndex = integration.dictionaries.findIndex((dictionary: DictionaryModel) =>
                dictionary.dictionaryName
                && (dictionary.dictionaryName.toLocaleLowerCase() === action.payload.dictionaryName.toLocaleLowerCase()));

            if (dictionaryIndex > -1) {
                integration.dictionaries[dictionaryIndex] = action.payload.dictionary;
            } else {
                integration.dictionaries.push(action.payload.dictionary);
            }
            integration.dictionaries = integration.dictionaries.filter((dictionary: DictionaryModel) => !!dictionary);

            return <State>{
                ...state,
                integration: integration
            };
        }
        case (ApplicationSettingsActions.ASSIGN_INTEGRATION_PROCESS_CONFIG_ITEM): {
            const integration: Integration = <Integration>{...state.integration};
            if (!integration.processConfigModel) {
                integration.processConfigModel = [];
            }
            const processConfigIndex = integration.processConfigModel
                .findIndex((pc: ProcessConfig) => {
                    if (action.payload.processId) {
                        return action.payload.processId === pc.processId;
                    } else {
                        return action.payload.incremental === pc.incremental;
                    }
                });
            if (processConfigIndex > -1) {
                integration.processConfigModel[processConfigIndex] = action.payload;
            } else {
                integration.processConfigModel.push(action.payload);
            }
            return <State>{
                ...state,
                integration: integration
            };
        }
        case (ApplicationSettingsActions.ASSIGN_INTEGRATION_PROCESS_CONFIG_MODEL): {
            const integration: Integration = <Integration>{...state.integration};

            if (integration.source) {
                const otherDS = (integration.source.dataSourceType === DataSourceType.EduTone) ? 'target' : 'source';
                integration.processConfigModel = ApplicationSettingsService.getExtendedProcessConfig(action.payload,
                    integration[otherDS].dataSourceType, state.integration.mode, state.integration[otherDS].defaultSchemaName);
            }

            return <State>{
                ...state,
                integration: integration
            };
        }
        case (ApplicationSettingsActions.ASSIGN_APPLICATION_EXPIRATION_DATE): {
            const application: Application = <Application>{...state.application};
            const integration: Integration = <Integration>{...state.integration};

            application.expirationTs = action.payload;
            integration.expirationTs = action.payload;

            return <State>{
                ...state,
                application: application,
                integration: integration
            };
        }
        case (ApplicationSettingsActions.ASSIGN_ACTUAL_PRIVACY_POLICY_DOCUMENT): {
            return <State>{
                ...state,
                actualPrivacyPolicyDocument: action.payload
            };
        }
        case (ApplicationSettingsActions.SET_MERGING_RULES): {
            const integration: Integration = <Integration>{...state.integration};
            const taskTargetTypeToTaskIdFileNameObj = {};

            if (integration.tasks) {
                integration.tasks = integration.tasks.map((task: IntegrationTask) => {
                    // add task.domainsOfUniqueness only to the one and the only first founded task
                    // if the are more than one tasks with the same task.targetType
                    if (!taskTargetTypeToTaskIdFileNameObj[task.targetType]) {
                        taskTargetTypeToTaskIdFileNameObj[task.targetType] = task.taskId;
                        task.domainsOfUniqueness = <DomainOfUniqueness[]>[];
                    }
                    return task;
                });
            }

            ApplicationSettingsService.sortMergingRulesByPropertiesFriendlyName(action.payload).forEach((mergingRule: DomainOfUniqueness) => {
                // add merging rule to the first founded task (if the are more than one tasks with the same task.targetType)
                const currentTask = integration.tasks.find((task: IntegrationTask) =>
                    task.taskId === taskTargetTypeToTaskIdFileNameObj[mergingRule.rosterTypeDenom]);

                if (currentTask) {
                    mergingRule.mergingRuleIndex = currentTask.domainsOfUniqueness.length;
                    const propertiesLength = mergingRule.properties.length;
                    for (let propertyIndex = 0; propertyIndex < propertiesLength; propertyIndex++) {
                        mergingRule.properties[propertyIndex].propertyIndex = propertyIndex;
                    }

                    currentTask.domainsOfUniqueness.push(mergingRule);
                }
            });

            return <State>{
                ...state,
                integration: integration
            };
        }
        case (ApplicationSettingsActions.ASSIGN_MERGING_RULES): {
            const integration: Integration = <Integration>{...state.integration};

            if (integration.tasks) {
                integration.tasks = integration.tasks.map((stateTask: IntegrationTask) => {
                    const task = action.payload.tasks.find((integrationTask: IntegrationTask) => stateTask.taskId === integrationTask.taskId);
                    if (task) {
                        stateTask.domainsOfUniqueness = task.domainsOfUniqueness;
                    }
                    return stateTask;
                });
            }

            return <State>{
                ...state,
                integration: integration
            };
        }
        case (ApplicationSettingsActions.SET_MASTER_FIELDS): {
            if (action.payload && action.payload.mergingMetadataModelList) {
                action.payload.mergingMetadataModelList = action.payload.mergingMetadataModelList
                    .filter((rule: MasterFieldsMergingMetadataModel) =>
                        !action.payload.mergingRuleList.some((existRule: MasterFieldsMergingRule) =>
                            existRule &&
                            existRule.propertyName == rule.propertyName &&
                            !(existRule.mode === MergeMode.MASTER || existRule.mode === MergeMode.SLAVE)
                        )
                    );

                // add mergingRuleIndex
                const entityList = <EntityMasterFieldsModel[]>[];
                action.payload.mergingMetadataModelList.forEach(rule => {
                    const entityIndex = entityList.findIndex((item: EntityMasterFieldsModel) => item.entityName === rule.entitySourceDname);
                    if (entityIndex === -1) {
                        entityList.push(new EntityMasterFieldsModel(rule.entitySourceDname));
                    }
                });

                const dataSourceConfig = state.dataSourceConfig;
                const dataSourceType = dataSourceConfig ? dataSourceConfig.dataSourceType : null;

                const pageAvailableTasksList = ApplicationSettingsService.getPageAvailableTasksList(
                    PAGES_WHERE_INTEGRATION_TASK_ARE_SHOWN.masterFields, dataSourceType, dataSourceConfig);

                // add mergingRuleIndex, filter unused mergingRules
                action.payload.mergingRuleList = action.payload.mergingRuleList
                    .filter((rule: MasterFieldsMergingRule) => !!rule
                        && pageAvailableTasksList.includes(rule.entityTypeDenom)
                        && ((<MergeMode>rule.mode === MergeMode.MASTER) || (<MergeMode>rule.mode === MergeMode.SLAVE)))
                    .map((rule: MasterFieldsMergingRule) => {

                        const entityIndex = entityList.findIndex((item: EntityMasterFieldsModel) => item.entityName === rule.entityTypeDenom);
                        if ((entityIndex > -1)) {
                            rule.mergingRuleIndex = entityList[entityIndex].mergingRuleList.length;
                            entityList[entityIndex].mergingRuleList.push(rule);
                        }

                        return rule;
                    });
            }

            return <State>{
                ...state,
                masterFields: action.payload
            };
        }
        case (ApplicationSettingsActions.ASSIGN_FILTRATIONS): {
            return <State>{
                ...state,
                filtrations: action.payload
            };
        }
        case (ApplicationSettingsActions.ASSIGN_SSO_FILTRATIONS): {
            return <State>{
                ...state,
                ssoFiltrations: action.payload
            };
        }
        case (ApplicationSettingsActions.ASSIGN_MAPPING_RULES):
            return <State>{
                ...state,
                mappingRules: action.payload
            };
        case (ApplicationSettingsActions.SET_AUTO_PROVISIONING):
            return <State>{
                ...state,
                webhook: action.payload
            };
        case (ApplicationSettingsActions.SET_PII_SHIELD_ENTITIES_ATTRIBUTES):
            return <State>{
                ...state,
                piiShieldConfigEntitiesAttributes: action.payload
            };
        case (ApplicationSettingsActions.SET_INTEGRATION_STATE):
            return <State>{
                ...state,
                integrationState: action.payload
            };
        case (ApplicationSettingsActions.SET_INTEGRATION_MODE):
            return <State>{
                ...state,
                integrationMode: action.payload
            };
        case (ApplicationSettingsActions.SET_PREDICATES_METADATA):
            return <State>{
                ...state,
                predicatesMetadata: action.payload
            };
        case (ApplicationSettingsActions.SET_FEATURE_TOGGLES):
            const applicationSettingsFeatureToggles = Object.assign({}, state.applicationSettingsFeatureToggles, action.payload);
            return <State>{
                ...state,
                applicationSettingsFeatureToggles: applicationSettingsFeatureToggles
            };
        case (ApplicationSettingsActions.APPLICATION_CLEAR_STATE): {
            // return initialState;
            return {
                defaultApplications: <Application[]>[],
                allowedApplications: <Application[]>[],
                ftpConfig: new FTPConfig(),
                dataSourceConfig: new DataSourceConfig(),
                mappingRulesArchiveOU: null,
                application: new Application(),
                actualPrivacyPolicyDocument: null,
                isDeletingApplication: false,
                integration: new Integration(),
                orgsMapping: null,
                predicatesMetadata: <MetadataModel[]>[],
                filtrations: null,
                ssoFiltrations: null,
                mappingRules: null,
                masterFields: null,
                integrationMode: null,
                integrationState: null,
                isLoading: false,
                isSavingFTPUser: false,
                isOriginalIntegration: false,
                rosterAccessProgress: new RosterAccessProgress(100),
                qwiklabsCourses: <QwiklabsCourse[]>[],
                googleClassroomDSOptions: new GoogleClassroomDataSourceOptions(),
                webhook: null,
                piiShieldConfigEntitiesAttributes: [],
                applicationSettingsFeatureToggles: {application: <FeatureToggleModel[]>[], integration: <FeatureToggleModel[]>[]},
                userPermission: new UserPermissionModel(),
                error: null
            };
        }
        case (ApplicationSettingsActions.SET_IS_LOADING):
            return <State>{
                ...state,
                isLoading: action.payload
            };
        case (ApplicationSettingsActions.SET_IS_SAVING_FTP_USER):
            return <State>{
                ...state,
                isSavingFTPUser: action.payload
            };
        case (ApplicationSettingsActions.SET_IS_ORIGINAL_INTEGRATION):
            return <State>{
                ...state,
                isOriginalIntegration: action.payload
            };
        case (ApplicationSettingsActions.SET_ROSTER_ACCESS_PROGRESS):
            return <State>{
                ...state,
                rosterAccessProgress: action.payload
            };
        case (ApplicationSettingsActions.SET_QWIKLABS_COURSES): {
            let qwiklabsCourses = action.payload;
            if (qwiklabsCourses && qwiklabsCourses.length > 0) {
                qwiklabsCourses = qwiklabsCourses.map((qwiklabsCourse: QwiklabsCourse, order: number) => {
                    qwiklabsCourse.order = order;
                    return qwiklabsCourse;
                });
            }
            return <State>{
                ...state,
                qwiklabsCourses: qwiklabsCourses
            };
        }
        case (ApplicationSettingsActions.SET_GOOGLE_CLASSROOM_DATA_SOURCE_OPTIONS): {
            const googleClassroomDSOptions = Object.assign({}, state.googleClassroomDSOptions, action.payload);
            return <State>{
                ...state,
                googleClassroomDSOptions: googleClassroomDSOptions
            };
        }
        case (ApplicationSettingsActions.SET_ERROR):
            return <State>{
                ...state,
                error: action.payload
            };
        default:
            return state;
    }
}
